import { useCallback, useState } from 'react';

import { isRowApproved } from './approvalQueueModel';
import type { DealOptionsInclusionsChecklistRow } from './tabs/dealOptionsInclusions/types';
import type { ApprovalQueueItem, ManualApprovalState } from './types';

export function useChecklistApprovalState() {
  const [expandedQueueItems, setExpandedQueueItems] = useState<Record<string, boolean>>({});
  const [manualApprovals, setManualApprovals] = useState<ManualApprovalState>({});

  const resetApprovalState = useCallback(() => {
    setManualApprovals({});
    setExpandedQueueItems({});
  }, []);

  const approveRow = useCallback((rowId: string) => {
    setManualApprovals((current) => ({ ...current, [rowId]: true }));
  }, []);

  const unapproveRow = useCallback((rowId: string) => {
    setManualApprovals(({ [rowId]: _, ...rest }) => rest);
  }, []);

  const toggleQueueItemDetails = useCallback((itemId: string) => {
    setExpandedQueueItems((current) => ({ ...current, [itemId]: !current[itemId] }));
  }, []);

  const expandVisibleQueueItems = useCallback((visibleQueueItems: Array<ApprovalQueueItem>) => {
    setExpandedQueueItems((current) => {
      const shouldExpand = !visibleQueueItems.every((queueItem) => current[queueItem.id]);

      return visibleQueueItems.reduce(
        (next, queueItem) => ({
          ...next,
          [queueItem.id]: shouldExpand,
        }),
        current,
      );
    });
  }, []);

  const markDealOptionsInclusionsApproved = useCallback((rows: Array<DealOptionsInclusionsChecklistRow>) => {
    setManualApprovals((current) => {
      const ids = rows.filter((row) => row.status !== 'blocked' && !isRowApproved(row, current)).map((row) => row.id);

      return {
        ...current,
        ...Object.fromEntries(ids.map((id) => [id, true])),
      };
    });
  }, []);

  return {
    approveRow,
    expandedQueueItems,
    expandVisibleQueueItems,
    manualApprovals,
    markDealOptionsInclusionsApproved,
    resetApprovalState,
    toggleQueueItemDetails,
    unapproveRow,
  };
}
